import React, { useState } from 'react';
import {
  Task,
  TaskStatus,
  updateTask as UPDATE_TASK,
  ReviewerFeedbackStatus,
} from '@admiin-com/ds-graphql';
import {
  WBBox,
  WBButton,
  WBFlex,
  WBTextFieldV2,
  WBTypography,
  WBSplitButton,
  useSnackbar,
} from '@admiin-com/ds-web';
import { styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMutation, gql } from '@apollo/client';
import { useDocumentUrl } from '../../../hooks/useDocumentUrl/useDocumentUrl';
import SignPayLayout from '../layout/SignPayLayout';
import PdfViewer from '../../../components/PdfViewer/PdfViewer';
import { TaskDetailForm } from '../../components/TaskDetail/components/TaskDetailForm';
import { RejectModal } from './RejectModal';
import { useUserId } from '../../../hooks/useCurrentUser/useCurrentUser';
import { SupportDoc } from '../../components/TaskDetail/components/SupportDoc';

interface TaskReviewContentProps {
  task: Task;
  onClose: () => void;
  onSuccess?: () => void;
  onEdit?: () => void;
}

export const TaskReviewContent = ({
  task,
  onClose,
  onSuccess,
  onEdit,
}: TaskReviewContentProps) => {
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const userId = useUserId();
  const { documentUrl } = useDocumentUrl(task);
  const [emailMessage, setEmailMessage] = useState(task.emailMessage ?? '');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const [updateTaskMutation] = useMutation(gql(UPDATE_TASK));

  const handleApprove = async () => {
    setLoading(true);
    try {
      await updateTaskMutation({
        variables: {
          input: {
            id: task.id,
            entityId: task.entityId,
            status: TaskStatus.INCOMPLETE,
            emailMessage: emailMessage,
          },
        },
      });
      showSnackbar({
        message: t('taskApproved', {
          ns: 'taskbox',
          defaultValue: 'Task approved and sent to client',
        }),
        severity: 'success',
        horizontal: 'right',
        vertical: 'bottom',
      });
      onSuccess?.();
      onClose();
    } catch (error: any) {
      showSnackbar({
        message:
          error?.message ??
          t('taskApproveFailed', {
            ns: 'taskbox',
            defaultValue: 'Failed to approve task',
          }),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    // Close the review modal and trigger the edit callback if provided
    onClose();
    onEdit?.();
  };

  const handleReject = () => {
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async (rejectionNote: string) => {
    setLoading(true);
    try {
      // Create new feedback entry while preserving existing feedback history
      const existingFeedback = task.reviewerFeedback ?? [];
      const newFeedback = {
        reviewedById: userId,
        feedback: rejectionNote,
        status: ReviewerFeedbackStatus.PENDING,
        createdAt: new Date().toISOString(),
      };

      await updateTaskMutation({
        variables: {
          input: {
            id: task.id,
            entityId: task.entityId,
            status: TaskStatus.DRAFT,
            reviewerFeedback: [...existingFeedback, newFeedback],
          },
        },
      });
      showSnackbar({
        message: t('taskRejected', {
          ns: 'taskbox',
          defaultValue: 'Task rejected and sent back to creator',
        }),
        severity: 'success',
        horizontal: 'right',
        vertical: 'bottom',
      });
      setRejectModalOpen(false);
      onSuccess?.();
      onClose();
    } catch (error: any) {
      showSnackbar({
        message:
          error?.message ??
          t('taskRejectFailed', {
            ns: 'taskbox',
            defaultValue: 'Failed to reject task',
          }),
        severity: 'error',
        horizontal: 'right',
        vertical: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <SignPayLayout title={t('taskReview', { ns: 'taskbox' })} onClose={onClose}>
      <Container>
        {/* Left Side - PDF Viewer */}
        <PdfSection>
          {documentUrl && <PdfViewer documentUrl={documentUrl} height="100%" />}
        </PdfSection>

        {/* Right Side - Task Details Panel */}
        <DetailsSection>
          <ScrollableContent>
            <WBFlex flexDirection="column" gap={1.5}>
              {/* Task Details (Read-only) */}
              <CompactSectionBox>
                <TaskDetailForm task={task} />
              </CompactSectionBox>

              {/* Email Message (Editable) */}
              <CompactSectionBox>
                <WBTypography variant="body2" fontWeight="bold" mb={1}>
                  {t('emailMessage', {
                    ns: 'taskbox',
                    defaultValue: 'Email Message',
                  })}
                </WBTypography>
                <WBTextFieldV2
                  multiline
                  rows={3}
                  value={emailMessage}
                  onChange={(e) => setEmailMessage(e.target.value)}
                  placeholder={t('emailMessagePlaceholder', {
                    ns: 'taskbox',
                    defaultValue: 'Write a message to the recipient',
                  })}
                  fullWidth
                  size="small"
                />
              </CompactSectionBox>

              {/* Supporting Documents */}
              {task.supportDocuments && task.supportDocuments.length > 0 && (
                <CompactSectionBox>
                  <SupportDoc task={task} />
                </CompactSectionBox>
              )}
            </WBFlex>
          </ScrollableContent>

          {/* Action Buttons - Always visible */}
          <ActionButtonsContainer>
            <WBButton
              variant="outlined"
              color="primary"
              onClick={handleEdit}
              disabled={loading}
            >
              {t('edit', { ns: 'taskbox', defaultValue: 'Edit' })}
            </WBButton>
            <WBSplitButton
              mainAction={{
                label: t('approve', {
                  ns: 'taskbox',
                  defaultValue: 'Approve',
                }),
                onClick: handleApprove,
              }}
              options={[
                {
                  label: t('reject', {
                    ns: 'taskbox',
                    defaultValue: 'Reject',
                  }),
                  onClick: handleReject,
                },
              ]}
              disabled={loading}
              loading={loading}
            />
          </ActionButtonsContainer>
        </DetailsSection>
      </Container>

      {/* Reject Modal */}
      <RejectModal
        open={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleRejectConfirm}
        loading={loading}
      />
    </SignPayLayout>
  );
};

const Container = styled(WBFlex)(({ theme }) => ({
  width: '100%',
  height: '100%',
  flexDirection: 'row',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const PdfSection = styled(WBBox)(({ theme }) => ({
  flex: 2,
  minHeight: '500px',
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
}));

const DetailsSection = styled(WBBox)(({ theme }) => ({
  flex: 1,
  minWidth: '350px',
  maxWidth: '450px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    minWidth: 'auto',
  },
}));

const ScrollableContent = styled(WBBox)({
  flex: 1,
  overflowY: 'auto',
});

const CompactSectionBox = styled(WBBox)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
}));

const ActionButtonsContainer = styled(WBFlex)(({ theme }) => ({
  justifyContent: 'space-between',
  flexShrink: 0,
  backgroundColor: theme.palette.background.default,
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
}));
